import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { ComponentTextXSmall, TextProps } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { FormattedDate, FormattedTime } from 'react-intl';

import { formatCircleFormat } from './utils/formatCircleFormat';
import { getUniqueFacilitatorPhotos } from './utils/getUniqueFacilitatorPhotos';
import { formatCircleBackgroundStyle } from './utils/formatCircleBackground';
import { CircleTopicFragment } from '../documents/topic.generated';

const CardWrapper = styled.div({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  padding: 15,
  border: `2px solid ${colors.gray05}`,
  borderRadius: 6,
  position: 'relative',
  cursor: 'pointer',
});

const CardTextWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flex: 1,
});

interface TopicTypeTextProps {
  textColor: string;
}

const TopicTypeText = styled(ComponentTextXSmall)<TopicTypeTextProps>(({ textColor }) => ({
  color: textColor,
  textTransform: 'uppercase',
}));

interface ProfilePicProps {
  borderColor: string;
}

const ProfilePic = styled('img')<ProfilePicProps>(({ borderColor }) => ({
  borderRadius: '100%',
  height: 50,
  width: 50,
  borderStyle: 'solid',
  borderWidth: 3,
  borderColor,
}));

export const TopicCard: React.FC<{
  topic: CircleTopicFragment;
  header: React.FC<TextProps>;
  body: React.FC<TextProps>;
  info?: React.ReactElement;
  sessionId?: string;
}> = ({ topic, sessionId, header: Header, body: Body, info = null, children }) => {
  const edges = topic?.sessions.edges;
  const selectedSession = edges.filter(edge => edge?.node.id === sessionId)[0]?.node;

  const photos = useMemo(() => getUniqueFacilitatorPhotos(topic, 4), [topic]);

  const sessionFormat = formatCircleFormat(topic?.format);
  const { Image, borderColor, textColor } = formatCircleBackgroundStyle(topic?.format);
  const session = selectedSession;

  return (
    <CardWrapper>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          marginLeft: 16,
          marginBottom: 16,
        }}
      >
        {photos.map(photo => (
          <ProfilePic
            src={photo}
            key={btoa(photo)}
            alt=""
            borderColor={borderColor}
            css={{
              marginLeft: -16,
            }}
          />
        ))}
      </div>
      <CardTextWrapper>
        <div>
          <TopicTypeText css={{ marginBottom: 4 }} textColor={textColor}>
            {sessionFormat}
          </TopicTypeText>
          <Header marginBottom={4}>
            {/* Temporarily append a space to every topic name to avoid cutting off Emoji on some systems */}
            {topic.name}{' '}
          </Header>
          {session && (
            <Body css={{ marginBottom: 8 }} fontColor="gray65">
              <FormattedDate
                day="numeric"
                month="short"
                weekday="short"
                value={session.startTime}
              />
              {' · '}
              <FormattedTime
                hour="numeric"
                minute="numeric"
                timeZoneName="short"
                value={session.startTime}
              />
              {info ?? null}
            </Body>
          )}
        </div>
        {children}
      </CardTextWrapper>
      <Image
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          height: 55,
          width: 100,
        }}
      />
    </CardWrapper>
  );
};
